import { useCallback, useMemo } from 'react';

import { useUser, useUserUpdate } from '@auth-v2';
import { format, isValid, parseISO } from 'date-fns';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';

import {
    AvailablePlaybackSpeed,
    CameraAngle,
    Coach,
    FloorSettings as FloorSettingsModel,
    LocaleCode,
    UserCoach,
    UserPreferredUnit,
    UserSettings as UserSettingsModel,
    UserSex,
} from '@data';
import { useUserSettingsWithDefaults } from '@hooks';
import { useTranslate } from '@i18n';
import {
    useCoach,
    useDeleteCoach,
    useFloorSettings,
    useUpdateUserCoaches,
    useUpdateUserSettings,
    useUpsertCoach,
    useUpsertFloorSettings,
    useUserCoaches,
} from '@queries';
import { Inline, Spinner, mdAbove } from '@ui';

import {
    AccountSection,
    BagSection,
    CancelButton,
    CoachProfileSection,
    CoachSection,
    CommitSha,
    DateOfBirthSection,
    FloorSettings,
    GolfHandicapSection,
    HandednessConfirmationModal,
    LanguagePicker,
    LeftHanded,
    SaveButton,
    SexSection,
    UnitSection,
    UserSettingsRoot,
} from './components';
import { LiveView } from './components/LiveView/LiveView';
import { useHandednessConfirmation } from './useHandednessConfirmation';
import * as css from './UserSettings.css';
import { useSetLocaleAtom } from './useSetLocale';

export type CoachForm = {
    location: string;
    pgaCertified: boolean;
    elvaCertified: boolean;
};

export type UserSettingsForm = {
    name: string;
    email: string;
    dateOfBirth?: Date | null;
    sex?: UserSex | null;
    handicap: number;
    userPreferredUnit: UserPreferredUnit;
    locale: LocaleCode;
    coachIds: string[];
    isCoach: boolean;
    leftHanded: boolean;
    liveView: boolean;
    coach: CoachForm;
    instantPlayback: boolean;
    playbackSpeed: AvailablePlaybackSpeed;
    cameraAngle?: CameraAngle | null;
};

const parseDob = (value: string | null | undefined) => {
    if (!value) {
        return null;
    }

    const date = parseISO(value);

    return isValid(date) ? date : null;
};

export function UserSettings({ onCreateNewActivity }: Pick<UserSettingsProps, 'onCreateNewActivity'>) {
    const t = useTranslate();
    const { data: userSettings, isLoading, error } = useUserSettingsWithDefaults();
    const { data: userCoaches, isLoading: isLoadingUserCoached } = useUserCoaches();
    const { data: coachData, isLoading: isLoadingCoachData } = useCoach();
    const { data: floorSettings, isLoading: isLoadingFloorSettings } = useFloorSettings();
    if (isLoading || isLoadingUserCoached || isLoadingCoachData || isLoadingFloorSettings) {
        return (
            <UserSettingsRoot
                end={
                    <>
                        <CancelButton />
                        <SaveButton isDisabled={true} isLoading={isLoading} />
                    </>
                }
            >
                <Spinner size="l" />
            </UserSettingsRoot>
        );
    }
    if (error || !userSettings) {
        return (
            <UserSettingsRoot
                end={
                    <>
                        <CancelButton />
                        <SaveButton isDisabled={true} isLoading={isLoading} />
                    </>
                }
            >
                <code>{error?.message ?? t('user_settings.load_error')}</code>
            </UserSettingsRoot>
        );
    }
    return (
        <UserSettingsScreen
            userSettings={userSettings}
            userCoaches={userCoaches}
            coachData={coachData}
            floorSettings={floorSettings}
            onCreateNewActivity={onCreateNewActivity}
        />
    );
}

type UserSettingsProps = {
    userSettings: Omit<UserSettingsModel, 'userId'>;
    coachData: Coach | null | undefined;
    userCoaches: UserCoach[] | undefined;
    floorSettings: FloorSettingsModel | undefined;
    onCreateNewActivity?: () => Promise<number>;
};

function UserSettingsScreen({
    userSettings,
    coachData,
    userCoaches,
    floorSettings,
    onCreateNewActivity,
}: UserSettingsProps) {
    const { mutateAsync: updateUserSettings } = useUpdateUserSettings();
    const { mutateAsync: updateFloorSettings } = useUpsertFloorSettings();
    const { data: user } = useUser();
    const { mutateAsync: updateUser } = useUserUpdate();
    const updateUserCoachesMutation = useUpdateUserCoaches(user?.id || '');
    const upsertCoachMutation = useUpsertCoach();
    const deleteCoachMutation = useDeleteCoach();
    const coachIds = useMemo(() => userCoaches?.map((relation) => relation.coachId) ?? [], [userCoaches]);

    const navigate = useNavigate();

    const persistSettings = useCallback(
        async (
            {
                name,
                email,
                dateOfBirth,
                sex,
                handicap,
                locale,
                leftHanded,
                userPreferredUnit,
                coachIds,
                isCoach,
                coach,
                instantPlayback,
                cameraAngle,
                playbackSpeed,
                liveView,
            }: UserSettingsForm,
            // if user confirmed that he is playing in different handedness we create new activity
            // this boolean is the user confirmation
            createNewActivity?: boolean,
        ) => {
            /**
             * TODO: reimplement
             */

            if (name !== user?.user_metadata['name'] || email !== user?.email) {
                await updateUser({ name, email });
            }
            await Promise.all([
                updateUserSettings({
                    dateOfBirth: dateOfBirth ? format(dateOfBirth, 'yyyy-MM-dd') : null,
                    sex: sex ?? null,
                    handicap,
                    userPreferredUnit: userPreferredUnit,
                    locale: locale,
                    leftHanded: leftHanded,
                    liveView,
                }),
                updateFloorSettings({ instantPlayback, cameraAngle, playbackSpeed }),
            ]);
            // Update coach selection:
            await updateUserCoachesMutation.mutateAsync(coachIds);

            // Update coach profile
            if (isCoach) {
                await upsertCoachMutation.mutateAsync({
                    location: coach.location,
                    pgaCertified: !!coach.pgaCertified,
                    elvaCertified: !!coach.elvaCertified,
                });
            } else if (coachData) {
                await deleteCoachMutation.mutateAsync();
            }
            if (createNewActivity) {
                await onCreateNewActivity?.();
                return;
            }

            navigate(-1);
        },
        [
            coachData,
            deleteCoachMutation,
            navigate,
            onCreateNewActivity,
            updateFloorSettings,
            updateUser,
            updateUserCoachesMutation,
            updateUserSettings,
            upsertCoachMutation,
            user?.email,
            user?.user_metadata,
        ],
    );

    const { handleSave, confirm, dismiss, isOpen } = useHandednessConfirmation({
        currentLeftHanded: userSettings.leftHanded,
        onConfirmSave: persistSettings,
        onCreateNewActivity,
    });

    const methods = useForm<UserSettingsForm>({
        defaultValues: {
            name: user?.user_metadata?.['name'],
            email: user?.email,
            dateOfBirth: parseDob(userSettings.dateOfBirth),
            sex: userSettings.sex ?? null,
            handicap: userSettings.handicap ?? 54,
            userPreferredUnit: userSettings.userPreferredUnit ?? 'metric',
            locale: userSettings.locale ?? 'en',
            coachIds: coachIds,
            isCoach: !!coachData,
            leftHanded: userSettings.leftHanded,
            liveView: userSettings.liveView,
            instantPlayback: floorSettings?.instantPlayback,
            cameraAngle: floorSettings?.cameraAngle,
            playbackSpeed: floorSettings?.playbackSpeed,
            coach: {
                location: coachData?.location ?? '',
                pgaCertified: coachData?.pgaCertified,
                elvaCertified: coachData?.elvaCertified,
            },
        },
    });
    useSetLocaleAtom(methods);
    return (
        <FormProvider {...methods}>
            <UserSettingsRoot
                start={
                    <div className={mdAbove}>
                        <Inline gap={16} alignItems="center">
                            <LanguagePicker />
                        </Inline>
                    </div>
                }
                end={
                    <>
                        <CancelButton />
                        <SaveButton onClick={methods.handleSubmit(handleSave)} />
                    </>
                }
            >
                <AccountSection />
                <UnitAndCoachInfoWrapper />
                {/* <LanguageAndPasswordWrapper onChangePassword={() => setModalOpen(true)} /> */}
                <FloorSettings />
                <BagSection activeUserId={user?.id} />

                <CommitSha />

                {isOpen && <HandednessConfirmationModal onConfirm={confirm} onDismiss={dismiss} />}
            </UserSettingsRoot>
        </FormProvider>
    );
}

const UnitAndCoachInfoWrapper = () => {
    const { watch } = useFormContext<UserSettingsForm>();
    const isCoach = watch('isCoach');
    return (
        <div className={css.settingsGroupsWrapper}>
            <DateOfBirthSection />
            <SexSection />
            <GolfHandicapSection />
            <CoachSection />
            {isCoach && <CoachProfileSection />}
            <UnitSection />
            <LeftHanded />
            <LiveView />
        </div>
    );
};
